import 'leaflet/dist/leaflet.css';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import type { LatLngExpression } from 'leaflet';
import type { Place } from '@map/shared';
import { markerIcon } from './markers.js';

/**
 * Centre et zoom couvrant le Kazakhstan, l'Ouzbekistan et le Kirghizistan
 * d'un seul coup d'oeil. Valeurs fixes : il n'y a qu'un voyage par instance,
 * memoriser la derniere vue apporterait peu et surprendrait au partage d'un
 * lien entre amis.
 */
const CENTER: LatLngExpression = [42.5, 68.5];
const ZOOM = 5;

const OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
// L'attribution est une obligation de la licence ODbL des tuiles OSM, pas
// une politesse : elle ne doit jamais etre retiree du rendu.
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/**
 * La route est tracee en deux passes : un fourreau sombre dessous, le trait
 * safran tirete dessus. Sur des tuiles chargees (foret, relief, zone urbaine)
 * un trait simple se perd ; le fourreau lui garantit un contraste constant.
 */
const ROUTE_CASING = { color: '#0e191b', weight: 6, opacity: 0.22 } as const;
const ROUTE_LINE = {
  color: '#e9a13b',
  weight: 3.5,
  opacity: 1,
  dashArray: '0.1 10',
  lineCap: 'round',
} as const;

/**
 * Cadrage demande de l'exterieur (recherche de lieu).
 *
 * `token` change a chaque demande : sans lui, choisir deux fois le meme
 * resultat apres avoir deplace la carte a la main ne recentrerait pas la
 * seconde fois, puisque lat/lng/zoom seraient identiques.
 */
export interface MapFocus {
  readonly lat: number;
  readonly lng: number;
  readonly zoom: number;
  readonly token: number;
}

export interface TripMapProps {
  readonly places: readonly Place[];
  readonly itinerary: readonly Place[];
  readonly scoreOf: (placeId: number) => number;
  readonly selectedId: number | null;
  readonly onSelect: (placeId: number) => void;
  /** Quand vrai, le prochain clic sur le fond de carte cree un lieu. */
  readonly addMode: boolean;
  readonly onPick: (lat: number, lng: number) => void;
  /** Cadrage demande par la recherche de lieu. Null tant que rien n'est cherche. */
  readonly focus: MapFocus | null;
}

/**
 * Le mode ajout est EXPLICITE. Sans ce garde-fou, chaque deplacement rate de
 * la carte au doigt deposerait un lieu fantome, et le vivier se remplirait de
 * brouillon a nettoyer.
 */
function ClickCatcher({
  enabled,
  onPick,
}: {
  readonly enabled: boolean;
  readonly onPick: (lat: number, lng: number) => void;
}): null {
  useMapEvents({
    click: (mapEvent) => {
      if (!enabled) return;
      onPick(mapEvent.latlng.lat, mapEvent.latlng.lng);
    },
  });
  return null;
}

/**
 * Applique un cadrage venu de l'exterieur.
 *
 * L'instance Leaflet n'est accessible que sous `<MapContainer>` : d'ou ce
 * composant sans rendu, plutot qu'une ref remontee jusqu'a l'ecran principal.
 */
function FocusFlyer({ focus }: { readonly focus: MapFocus | null }): null {
  const map = useMap();
  const lastToken = useRef<number | null>(null);

  useEffect(() => {
    if (focus === null || focus.token === lastToken.current) return;
    lastToken.current = focus.token;

    // Le vol anime raconte le deplacement, ce qui evite de se demander ou l'on
    // vient d'atterrir. Mais qui demande moins d'animation recoit un saut sec.
    const calm =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (calm) map.setView([focus.lat, focus.lng], focus.zoom);
    else map.flyTo([focus.lat, focus.lng], focus.zoom, { duration: 0.9 });
  }, [focus, map]);

  return null;
}

export function TripMap(props: TripMapProps): ReactNode {
  const line = useMemo<LatLngExpression[]>(
    () => props.itinerary.map((place) => [place.lat, place.lng] as LatLngExpression),
    [props.itinerary],
  );

  // Rang de chaque etape, pour que le marqueur affiche son numero d'ordre.
  const stepOf = useMemo(() => {
    const ranks = new Map<number, number>();
    props.itinerary.forEach((place, index) => ranks.set(place.id, index + 1));
    return ranks;
  }, [props.itinerary]);

  return (
    <div className={`tripmap${props.addMode ? ' tripmap--adding' : ''}`}>
      <MapContainer className="tripmap__canvas" center={CENTER} zoom={ZOOM} scrollWheelZoom>
        <TileLayer url={OSM_URL} attribution={OSM_ATTRIBUTION} maxZoom={19} />

        <ClickCatcher enabled={props.addMode} onPick={props.onPick} />
        <FocusFlyer focus={props.focus} />

        {line.length >= 2 ? (
          <>
            <Polyline positions={line} pathOptions={ROUTE_CASING} interactive={false} />
            <Polyline positions={line} pathOptions={ROUTE_LINE} interactive={false} />
          </>
        ) : null}

        {props.places.map((place) => (
          <Marker
            key={place.id}
            position={[place.lat, place.lng]}
            icon={markerIcon({
              category: place.category,
              score: props.scoreOf(place.id),
              selected: props.selectedId === place.id,
              step: stepOf.get(place.id),
            })}
            eventHandlers={{ click: () => props.onSelect(place.id) }}
          >
            <Tooltip className="tripmap__tip" direction="top" offset={[0, -44]} opacity={1}>
              {place.title}
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>

      {props.addMode ? (
        <p className="tripmap__hint" role="status">
          <span className="tripmap__hint-dot" aria-hidden="true" />
          Clique sur la carte pour poser le lieu.
        </p>
      ) : null}
    </div>
  );
}
